import type { VatAmounts } from '$lib/money';
import type { OrderConfigurationOrder } from '$models/OrderConfigurator/OrderConfigurator';
import { browser } from '$app/environment';
import { parseMoney, subtractMoney } from '$lib/money';
import { webparking } from '$lib/webparking';

type Item = { id: string; quantity: number };
export type TotalPriceRequest = {
	frequency: number;
	order_items?: Item[];
	non_subscribable_products?: Item[];
	code?: string;
};
export type TotalPriceQuote = {
	beforeDiscount: VatAmounts;
	discount: VatAmounts;
	payable: VatAmounts | null;
	status: 'none' | 'applied' | 'rejected' | 'frequency-mismatch' | 'gift-selection-required';
	rejection: { code: string; message: string } | null;
	gifts: { id: string; name: string; value: VatAmounts; quantity: number; inStock: boolean }[];
	blockingPeriod: number | null;
	requiredOrderCount: number | null;
	maximumOrderFrequency: number | null;
};

function items(input: readonly Item[]): Item[] {
	const quantities = new Map<string, number>();
	for (const { id, quantity } of input) {
		if (!id || !Number.isInteger(quantity) || quantity < 0)
			throw new TypeError('Invalid basket item.');
		if (quantity === 0) continue;
		const total = (quantities.get(id) ?? 0) + quantity;
		if (total > 65535) throw new RangeError('Invalid basket quantity.');
		quantities.set(id, total);
	}
	return [...quantities]
		.sort(([a], [b]) => a.localeCompare(b))
		.map(([id, quantity]) => ({ id, quantity }));
}

/** Storefront quotes use the API's home-country defaults, independently of VAT presentation. */
export function totalPriceRequest(order: OrderConfigurationOrder): TotalPriceRequest {
	if (!Number.isInteger(order.frequency) || order.frequency < 0 || order.frequency > 65535) {
		throw new TypeError('Invalid frequency.');
	}
	const regular = items(order.products);
	const extras = items(order.nonSubscribableProducts);
	if (!regular.length && !extras.length) throw new TypeError('Empty basket.');
	const code = order.couponCode?.trim();
	return {
		frequency: order.frequency,
		...(regular.length ? { order_items: regular } : {}),
		...(extras.length ? { non_subscribable_products: extras } : {}),
		...(code ? { code } : {})
	};
}

function record(value: unknown): Record<string, unknown> {
	if (!value || typeof value !== 'object' || Array.isArray(value))
		throw new TypeError('Invalid quote.');
	return value as Record<string, unknown>;
}

function text(value: unknown): string {
	if (typeof value !== 'string') throw new TypeError('Invalid quote text.');
	return value;
}

function integer(value: unknown, minimum = 0): number {
	if (!Number.isSafeInteger(value) || (value as number) < minimum)
		throw new TypeError('Invalid quote quantity.');
	return value as number;
}

function nullableInteger(value: unknown, minimum = 0): number | null {
	return value == null ? null : integer(value, minimum);
}

function amounts(inclusive: unknown, exclusive: unknown): VatAmounts {
	const result = { inclusiveMinor: parseMoney(inclusive), exclusiveMinor: parseMoney(exclusive) };
	if (result.exclusiveMinor > result.inclusiveMinor) throw new TypeError('Invalid VAT amounts.');
	return result;
}

export function decodeTotalPrice(value: unknown, request: TotalPriceRequest): TotalPriceQuote {
	const data = record(value);
	const beforeDiscount = amounts(data.total_price, data.total_price_excluding_vat);
	const discount = amounts(data.discount_amount, data.discount_amount_excluding_vat);
	const discounted = subtractMoney(beforeDiscount, discount);
	if (
		!Array.isArray(data.free_non_subscribable_products) ||
		typeof data.selectable_free_non_subscribable_products !== 'boolean'
	) {
		throw new TypeError('Invalid quote gifts.');
	}
	const gifts = data.free_non_subscribable_products.map((value) => {
		const gift = record(value);
		if (typeof gift.in_stock !== 'boolean') throw new TypeError('Invalid gift stock.');
		return {
			id: text(gift.id),
			name: text(gift.name),
			quantity: integer(gift.quantity, 1),
			value: amounts(gift.price, gift.price_excluding_vat),
			inStock: gift.in_stock
		};
	});
	const reason =
		data.no_applicable_discount_reason === null ? null : record(data.no_applicable_discount_reason);
	let rejection = reason ? { code: text(reason.code), message: text(reason.message) } : null;
	const frequency = nullableInteger(data.frequency);
	const maximumOrderFrequency = nullableInteger(data.maximum_order_frequency);
	const blockingPeriod = nullableInteger(data.blocking_period);
	const requiredOrderCount = nullableInteger(data.required_order_count, 1);
	let status: TotalPriceQuote['status'];
	if (
		(frequency !== null && frequency !== request.frequency) ||
		(maximumOrderFrequency !== null && request.frequency > maximumOrderFrequency)
	) {
		status = 'frequency-mismatch';
		rejection = {
			code: status,
			message: 'Deze korting is niet geldig voor de gekozen bezorgfrequentie.'
		};
	} else if (rejection || (discount.inclusiveMinor === 0 && gifts.length === 0)) {
		status = request.code ? 'rejected' : 'none';
	} else {
		status = data.selectable_free_non_subscribable_products ? 'gift-selection-required' : 'applied';
	}
	return {
		beforeDiscount,
		discount,
		status,
		rejection,
		gifts,
		payable: status === 'applied' ? discounted : status === 'none' ? beforeDiscount : null,
		blockingPeriod: status === 'applied' ? blockingPeriod : null,
		requiredOrderCount: status === 'applied' ? requiredOrderCount : null,
		maximumOrderFrequency: status === 'applied' ? maximumOrderFrequency : null
	};
}

// Browser-only, short-lived cache. No customer data is shared between SSR requests.
const cache = new Map<string, { expires: number; quote: TotalPriceQuote }>();
const pending = new Map<string, Promise<TotalPriceQuote>>();
let generation = 0;

export function clearTotalPriceCache() {
	generation += 1;
	cache.clear();
	pending.clear();
}

export async function getTotalPrice(order: OrderConfigurationOrder): Promise<TotalPriceQuote> {
	const request = totalPriceRequest(order);
	const key = JSON.stringify(request);
	const currentGeneration = generation;
	const cached = browser ? cache.get(key) : undefined;
	if (cached && cached.expires > Date.now()) return structuredClone(cached.quote);
	let promise = browser ? pending.get(key) : undefined;
	if (!promise) {
		promise = (async () => {
			const response = await webparking.post('total-price', { json: request }).json<unknown>();
			if (currentGeneration !== generation) throw new Error('Pricing session changed.');
			const quote = decodeTotalPrice(response, request);
			if (browser) {
				cache.delete(key);
				cache.set(key, { expires: Date.now() + 60_000, quote });
				if (cache.size > 100) cache.delete(cache.keys().next().value!);
			}
			return quote;
		})().finally(() => {
			if (pending.get(key) === promise) pending.delete(key);
		});
		if (browser) pending.set(key, promise);
	}
	return structuredClone(await promise);
}
